import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Activity, Heart, Droplets, Scale, Calculator,
  TrendingUp, TrendingDown, Minus, ArrowLeft, Info, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreventiveHealthTracker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allMetrics, setAllMetrics] = useState({});
  const [activeMetric, setActiveMetric] = useState(null);
  // Ref to the metric entry form — used to smooth-scroll it into view
  // on mobile when a metric card is tapped. Without this, tapping a
  // card like "Cholesterol" or "BMI" appears to do nothing on small
  // viewports because the form is rendered below the 5-card grid and
  // falls off-screen.
  const formRef = useRef(null);

  // When the user opens a metric, scroll the form into view. We wait
  // a frame so React has committed the DOM update before measuring.
  useEffect(() => {
    if (activeMetric && formRef.current) {
      requestAnimationFrame(() => {
        try {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) {
          // Older browsers without smooth-scroll support — silent no-op.
        }
      });
    }
  }, [activeMetric]);
  
  // Form states
  const [bloodPressure, setBloodPressure] = useState({ systolic: '', diastolic: '' });
  const [bloodSugar, setBloodSugar] = useState({ value: '', unit: 'mg/dL' });
  const [cholesterol, setCholesterol] = useState({ total: '', hdl: '', ldl: '', triglycerides: '' });
  const [weight, setWeight] = useState({ value: '', unit: 'kg' });
  const [height, setHeight] = useState(''); // cm for BMI calculation
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchAllMetrics();
    }
  }, [user]);

  const fetchAllMetrics = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/health-metrics/history`, {
        withCredentials: true
      });
      setAllMetrics(data.metrics || {});
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to load health metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (metricType) => {
    setSubmitting(true);
    
    try {
      let value = {};
      
      switch(metricType) {
        case 'blood_pressure':
          if (!bloodPressure.systolic || !bloodPressure.diastolic) {
            toast.error('Please enter both systolic and diastolic values');
            setSubmitting(false);
            return;
          }
          value = {
            systolic: parseInt(bloodPressure.systolic),
            diastolic: parseInt(bloodPressure.diastolic)
          };
          break;
          
        case 'blood_sugar':
          if (!bloodSugar.value) {
            toast.error('Please enter blood sugar value');
            setSubmitting(false);
            return;
          }
          value = {
            value: parseFloat(bloodSugar.value),
            unit: bloodSugar.unit
          };
          break;
          
        case 'cholesterol':
          value = {
            total: cholesterol.total ? parseFloat(cholesterol.total) : null,
            hdl: cholesterol.hdl ? parseFloat(cholesterol.hdl) : null,
            ldl: cholesterol.ldl ? parseFloat(cholesterol.ldl) : null,
            triglycerides: cholesterol.triglycerides ? parseFloat(cholesterol.triglycerides) : null
          };
          break;
          
        case 'weight':
          if (!weight.value) {
            toast.error('Please enter weight');
            setSubmitting(false);
            return;
          }
          value = {
            value: parseFloat(weight.value),
            unit: weight.unit
          };
          break;
          
        case 'bmi':
          if (!weight.value || !height) {
            toast.error('Please enter both weight and height for BMI calculation');
            setSubmitting(false);
            return;
          }
          const weightKg = weight.unit === 'kg' ? parseFloat(weight.value) : parseFloat(weight.value) * 0.453592;
          const heightCm = parseFloat(height);
          const bmiValue = weightKg / ((heightCm / 100) ** 2);
          
          value = {
            value: parseFloat(bmiValue.toFixed(2)),
            weight_kg: weightKg,
            height_cm: heightCm
          };
          break;
      }
      
      await axios.post(`${API}/health-metrics/log`, {
        metric_type: metricType,
        value,
        notes: notes || null
      }, { withCredentials: true });
      
      toast.success('Health metric logged successfully!');
      
      // Reset form
      setBloodPressure({ systolic: '', diastolic: '' });
      setBloodSugar({ value: '', unit: 'mg/dL' });
      setCholesterol({ total: '', hdl: '', ldl: '', triglycerides: '' });
      setWeight({ value: '', unit: 'kg' });
      setNotes('');
      setActiveMetric(null);
      
      // Refresh data
      await fetchAllMetrics();
      
    } catch (error) {
      console.error('Error logging metric:', error);
      toast.error(error.response?.data?.detail || 'Failed to log health metric');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateBMI = () => {
    if (weight.value && height) {
      const weightKg = weight.unit === 'kg' ? parseFloat(weight.value) : parseFloat(weight.value) * 0.453592;
      const heightCm = parseFloat(height);
      const bmi = weightKg / ((heightCm / 100) ** 2);
      return bmi.toFixed(1);
    }
    return null;
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { text: 'Underweight', color: 'text-blue-600' };
    if (bmi < 25) return { text: 'Healthy', color: 'text-green-600' };
    if (bmi < 30) return { text: 'Overweight', color: 'text-yellow-600' };
    return { text: 'Obese', color: 'text-red-600' };
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'improving':
      case 'decreasing':
        return <TrendingDown className="h-4 w-4 text-green-600" />;
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-yellow-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const metricCards = [
    {
      type: 'blood_pressure',
      icon: Heart,
      title: 'Blood Pressure',
      description: 'Monitor your heart health',
      color: 'red',
      unit: 'mmHg'
    },
    {
      type: 'blood_sugar',
      icon: Droplets,
      title: 'Blood Sugar',
      description: 'Track glucose levels',
      color: 'blue',
      unit: 'mg/dL'
    },
    {
      type: 'cholesterol',
      icon: Activity,
      title: 'Cholesterol',
      description: 'Keep cholesterol in check',
      color: 'purple',
      unit: 'mg/dL'
    },
    {
      type: 'weight',
      icon: Scale,
      title: 'Weight',
      description: 'Track your weight trends',
      color: 'green',
      unit: 'kg/lbs'
    },
    {
      type: 'bmi',
      icon: Calculator,
      title: 'BMI',
      description: 'Body mass index',
      color: 'amber',
      unit: ''
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthloop-mint-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading health metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 md:top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Preventive Health Tracker</h1>
              <p className="text-sm text-gray-600">Track key metrics and take action before problems escalate</p>
            </div>
          </div>
        </div>
      </header>

      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-[calc(8rem+env(safe-area-inset-bottom))] md:pb-6"
      >
        {/* Medical Disclaimer */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-900 font-medium">Important Notice</p>
                <p className="text-xs text-amber-800 mt-1">
                  HealthLoop supports awareness and tracking. It does not diagnose or replace professional medical care. Always consult your healthcare provider for medical advice.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metric Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {metricCards.map((metric) => {
            const Icon = metric.icon;
            const data = allMetrics[metric.type] || {};
            const latestValue = data.latest_value;
            
            return (
              <Card 
                key={metric.type}
                className={`cursor-pointer hover:shadow-lg active:scale-[0.98] transition-all duration-150 border-2 border-${metric.color}-100 hover:border-${metric.color}-300`}
                onClick={() => setActiveMetric(metric.type)}
                data-testid={`metric-card-${metric.type}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className={`p-2 bg-${metric.color}-50 rounded-lg`}>
                      <Icon className={`h-5 w-5 text-${metric.color}-600`} />
                    </div>
                    <Plus className={`h-4 w-4 text-${metric.color}-400`} />
                  </div>
                  <CardTitle className="text-base mt-2">{metric.title}</CardTitle>
                  <CardDescription className="text-xs">{metric.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {latestValue ? (
                    <div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {metric.type === 'blood_pressure' && `${latestValue.systolic}/${latestValue.diastolic}`}
                        {metric.type === 'blood_sugar' && `${latestValue.value}`}
                        {metric.type === 'cholesterol' && `${latestValue.total || 'N/A'}`}
                        {metric.type === 'weight' && `${latestValue.value}`}
                        {metric.type === 'bmi' && `${latestValue.value}`}
                        <span className="text-sm text-gray-600 ml-1">{metric.unit}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Badge variant="outline" className="text-xs">
                          {data.total_entries || 0} entries
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data yet. Tap to add.</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Active Metric Form */}
        {activeMetric && (
          <Card
            ref={formRef}
            className="border-2 border-healthloop-mint-300"
            data-testid="metric-entry-form"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  Log {metricCards.find(m => m.type === activeMetric)?.title}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setActiveMetric(null)}>
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Blood Pressure Form */}
              {activeMetric === 'blood_pressure' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="systolic">Systolic (top number)</Label>
                      <Input
                        id="systolic"
                        type="number"
                        placeholder="120"
                        value={bloodPressure.systolic}
                        onChange={(e) => setBloodPressure({ ...bloodPressure, systolic: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="diastolic">Diastolic (bottom number)</Label>
                      <Input
                        id="diastolic"
                        type="number"
                        placeholder="80"
                        value={bloodPressure.diastolic}
                        onChange={(e) => setBloodPressure({ ...bloodPressure, diastolic: e.target.value })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">Normal: Less than 120/80 mmHg</p>
                </div>
              )}

              {/* Blood Sugar Form */}
              {activeMetric === 'blood_sugar' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="bloodSugar">Blood Sugar Level</Label>
                    <Input
                      id="bloodSugar"
                      type="number"
                      step="0.1"
                      placeholder="100"
                      value={bloodSugar.value}
                      onChange={(e) => setBloodSugar({ ...bloodSugar, value: e.target.value })}
                    />
                  </div>
                  <p className="text-xs text-gray-600">Fasting normal: 70-100 mg/dL</p>
                </div>
              )}

              {/* Cholesterol Form */}
              {activeMetric === 'cholesterol' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="total">Total Cholesterol</Label>
                      <Input
                        id="total"
                        type="number"
                        placeholder="200"
                        value={cholesterol.total}
                        onChange={(e) => setCholesterol({ ...cholesterol, total: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="hdl">HDL (Good)</Label>
                      <Input
                        id="hdl"
                        type="number"
                        placeholder="50"
                        value={cholesterol.hdl}
                        onChange={(e) => setCholesterol({ ...cholesterol, hdl: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ldl">LDL (Bad)</Label>
                      <Input
                        id="ldl"
                        type="number"
                        placeholder="100"
                        value={cholesterol.ldl}
                        onChange={(e) => setCholesterol({ ...cholesterol, ldl: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="triglycerides">Triglycerides</Label>
                      <Input
                        id="triglycerides"
                        type="number"
                        placeholder="150"
                        value={cholesterol.triglycerides}
                        onChange={(e) => setCholesterol({ ...cholesterol, triglycerides: e.target.value })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">Desirable total: Less than 200 mg/dL</p>
                </div>
              )}

              {/* Weight Form */}
              {activeMetric === 'weight' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="weight">Weight</Label>
                      <Input
                        id="weight"
                        type="number"
                        step="0.1"
                        placeholder="70"
                        value={weight.value}
                        onChange={(e) => setWeight({ ...weight, value: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="weightUnit">Unit</Label>
                      <select
                        id="weightUnit"
                        className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                        value={weight.unit}
                        onChange={(e) => setWeight({ ...weight, unit: e.target.value })}
                      >
                        <option value="kg">kg</option>
                        <option value="lbs">lbs</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* BMI Form */}
              {activeMetric === 'bmi' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="bmiWeight">Weight</Label>
                      <Input
                        id="bmiWeight"
                        type="number"
                        step="0.1"
                        placeholder="70"
                        value={weight.value}
                        onChange={(e) => setWeight({ ...weight, value: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="bmiHeight">Height (cm)</Label>
                      <Input
                        id="bmiHeight"
                        type="number"
                        placeholder="175"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                      />
                    </div>
                  </div>
                  {calculateBMI() && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Your BMI:</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-3xl font-bold text-gray-900">{calculateBMI()}</p>
                        <p className={`text-sm font-medium ${getBMICategory(parseFloat(calculateBMI())).color}`}>
                          {getBMICategory(parseFloat(calculateBMI())).text}
                        </p>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Healthy range: 18.5 - 24.9</p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes (all forms) */}
              <div className="mt-4">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any additional notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Submit Button */}
              <Button 
                onClick={() => handleSubmit(activeMetric)}
                disabled={submitting}
                className="w-full mt-4 bg-healthloop-mint-600 hover:bg-healthloop-mint-700"
              >
                {submitting ? 'Logging...' : 'Log Metric'}
              </Button>

              {/* Recent History */}
              {allMetrics[activeMetric]?.entries?.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Recent History</h4>
                  <div className="space-y-2">
                    {allMetrics[activeMetric].entries.slice(0, 5).map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {activeMetric === 'blood_pressure' && `${entry.value.systolic}/${entry.value.diastolic} mmHg`}
                            {activeMetric === 'blood_sugar' && `${entry.value.value} ${entry.value.unit}`}
                            {activeMetric === 'cholesterol' && `Total: ${entry.value.total || 'N/A'} mg/dL`}
                            {activeMetric === 'weight' && `${entry.value.value} ${entry.value.unit}`}
                            {activeMetric === 'bmi' && entry.value.value}
                          </p>
                          <p className="text-xs text-gray-600">{formatDate(entry.recorded_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PreventiveHealthTracker;
